<template>
  <div class="mall-quote">
    <div class="mall-quote__toolbar no-print">
      <router-link to="/mall/cart" class="mall-quote__back">
        <AppIcon icon="mdi-arrow-left" :size="18" />
        장바구니로
      </router-link>
      <div class="mall-quote__toolbar-actions">
        <button type="button" class="mall-quote__print" :disabled="isEmpty" @click="onPrint">
          <AppIcon icon="mdi-printer-outline" :size="18" />
          인쇄
        </button>
      </div>
    </div>

    <div v-if="!loaded || loading" class="mall-quote__loading" aria-busy="true">
      <span class="mall-quote__skel" />
      <span class="mall-quote__skel mall-quote__skel--wide" />
      <p>견적서를 준비하는 중입니다</p>
    </div>

    <div v-else-if="isEmpty" class="mall-quote__empty no-print">
      <AppIcon icon="mdi-file-document-outline" :size="40" />
      <p>장바구니가 비어 있어 견적서를 만들 수 없습니다.</p>
      <em>상품을 담은 뒤 다시 이용해 주세요.</em>
      <router-link to="/mall">쇼핑계속하기</router-link>
    </div>

    <article v-else class="mall-quote__sheet">
      <header class="mall-quote__head">
        <div>
          <p class="mall-quote__eyebrow">함우리몰 견적서</p>
          <h1>{{ orgName }}</h1>
          <p class="mall-quote__date">작성일 {{ issuedAt }}</p>
        </div>
      </header>

      <table class="mall-quote__table">
        <thead>
          <tr>
            <th>상품</th>
            <th class="is-num">수량</th>
            <th class="is-num">단가</th>
            <th class="is-num">금액</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in lines" :key="line.key">
            <td>
              <strong>{{ line.product.name }}</strong>
              <span v-if="line.optionName">옵션 · {{ line.optionName }}</span>
              <span v-if="line.product.unit">{{ line.product.unit }}</span>
            </td>
            <td class="is-num">{{ line.qty }}</td>
            <td class="is-num">{{ formatPrice(line.unitPrice) }}원</td>
            <td class="is-num">{{ formatPrice(line.lineTotal) }}원</td>
          </tr>
        </tbody>
      </table>

      <dl class="mall-quote__totals">
        <div>
          <dt>상품금액</dt>
          <dd>{{ formatPrice(listSubtotal) }}원</dd>
        </div>
        <div>
          <dt>주문 배송비</dt>
          <dd>{{ formatShippingFeeAmount(shippingFee) }}</dd>
        </div>
        <div class="is-total">
          <dt>합계</dt>
          <dd>{{ formatPrice(payable) }}원</dd>
        </div>
      </dl>

      <p class="mall-quote__note">
        본 견적서는 장바구니 기준이며, 재고·가격은 주문 시점에 따라 달라질 수 있습니다.
      </p>
    </article>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { ensureLiveProducts } from '@/api/mall'
import { useCart } from '@/composables/useCart'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { formatPrice } from '@/constants/mallProducts'
import { formatShippingFeeAmount } from '@/utils/mallShipping'

const QUOTE_PRINT_BODY_CLASS = 'is-mall-quote-print'

const {
  lines,
  isEmpty,
  loading,
  loaded,
  listSubtotal,
  shippingFee,
  payable,
  reload,
} = useCart()
const { member } = useMemberAuth()

const orgName = computed(() => member.value?.orgName || '기관명 미확인')
const issuedAt = computed(() =>
  new Date().toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }),
)

function onPrint() {
  if (isEmpty.value) return
  window.print()
}

onMounted(async () => {
  document.body.classList.add(QUOTE_PRINT_BODY_CLASS)
  await ensureLiveProducts()
  await reload()
})

onUnmounted(() => {
  document.body.classList.remove(QUOTE_PRINT_BODY_CLASS)
})
</script>

<style lang="scss" src="./MallCartQuotePage.scss"></style>
